import React, { useEffect, useReducer, useMemo, useState } from "react";
import { connect } from "react-redux";
import { v4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import PropTypes from "prop-types";
import {
  DropdownNonForm,
  LoadingIndicator,
  Button,
  Alert,
  formatDate
} from "@ufginsurance/ui-kit";
import { Translations } from "../../translations";
import { sortByProperty } from "../../../online-quoting/shared/util";
import { cloneDeep } from "lodash";
import SubmissionResults from "./SubmissionResults";
import HelpDeskMessage from "../../../help-desk/helpDeskMessage";
import { useNewQuoteButton } from "./useNewQuoteButton";
import { useFlags } from "launchdarkly-react-client-sdk";

const filterByKeyword = (keyword, submissions) => {
  const includesKeyword = value => {
    return !!value && value.toLowerCase().includes(keyword.toLowerCase());
  };

  return submissions.filter(
    item =>
      item &&
      (includesKeyword(item?.Customer?.name) ||
        includesKeyword(item?.Customer?.business_address?.address1) ||
        includesKeyword(item?.Customer?.business_address?.city) ||
        includesKeyword(item?.Customer?.business_address?.zip) ||
        includesKeyword(item?.Customer?.business_address?.state) ||
        includesKeyword(item?.quote_number) ||
        includesKeyword(item?.primaryAddress?.displayName) ||
        includesKeyword(item?.primaryAddress?.addressLine1) ||
        includesKeyword(item?.primaryAddress?.city) ||
        includesKeyword(item?.primaryAddress?.postalCode) ||
        includesKeyword(item?.primaryAddress?.state))
  );
};

const filterByStatus = (status, submissions) =>
  submissions.filter(
    item =>
      item &&
      item?.status &&
      status &&
      status?.value &&
      (item?.status || "").toLowerCase() === status.value.toLowerCase()
  );

export const filterSubmissions = (keyword, status, submissions) => {
  let filtered = [...submissions];
  if (status.value === "") {
    filtered = filtered.filter(
      item =>
        item && item.status !== "Inactive" && item.status !== "Deactivated"
    );
  } else {
    filtered = filterByStatus(status, filtered);
  }

  if (keyword !== "") {
    filtered = filterByKeyword(keyword, filtered);
  }

  // sort submssions in by date first so latest submissions appear in customer accordion
  filtered.sort(sortByProperty("last_updated", false));

  filtered.sort((a, b) => {
    const name1 = (a?.Customer?.name || "").toLowerCase();
    const name2 = (b?.Customer?.name || "").toLowerCase();
    return name1 > name2 ? 1 : name1 === name2 ? 0 : -1;
  });
  return filtered;
};

const initialState = {
  keyword: "",
  status: {
    label: "Show all *",
    value: ""
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "KEYWORD_CHANGED":
      return { ...state, keyword: action.keyword };
    case "STATUS_CHANGED":
      return { ...state, status: action.status };
    case "REFRESHED":
      return { ...initialState };
    default:
      throw new Error(`Unhandled action type: ${!!action ? action.type : ""}`);
  }
};

const NewSubmissionsContainer = ({
  fetchSubmissionList,
  submissionList,
  agencyCode,
  isLoading
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isALPLoaded, setIsALPLoaded] = useState(false);

  const { NewQuoteButton, QuotingModal } = useNewQuoteButton();
  const { disableOldProQuote } = useFlags();

  const handleRefresh = () => {
    dispatch({ type: "REFRESHED" });
    fetchSubmissionList(agencyCode);
  };

  const handleChange = e => {
    dispatch({ type: "KEYWORD_CHANGED", keyword: e.target.value });
  };

  const handleFilterChange = selectedStatus => {
    dispatch({ type: "STATUS_CHANGED", status: selectedStatus });
  };

  const waitForsFlagToLoad = () => {
    setTimeout(() => {
      setIsALPLoaded(true);
    }, 1000);
  };

  useEffect(() => {
    waitForsFlagToLoad();
    if (isALPLoaded && !submissionList.length) fetchSubmissionList(agencyCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isALPLoaded]);

  const filtered = useMemo(
    () => filterSubmissions(state.keyword, state.status, submissionList),
    [state.keyword, state.status, submissionList]
  );

  const effectiveDateSort = (a, b) => {
    try {
      if (
        formatDate(a?.effectiveDate, "ISO") <
        formatDate(b?.effectiveDate, "ISO")
      )
        return 1;
      else if (
        formatDate(a?.effectiveDate, "ISO") >
        formatDate(b?.effectiveDate, "ISO")
      )
        return -1;
      return 0;
    } catch (e) {
      //swallow all errorsAndWarnings
      return 0;
    }
  };

  const { actions, messages, status_options, control_labels } =
    Translations.commercial.new_submissions_tab;

  let priorCustomer = undefined;

  const clonedFiltered = cloneDeep(filtered);
  return (
    <div className="newSubmissionPanel">
      <QuotingModal />
      <div className="row search-submission-panel">
        <div>
          <div className="newSubmissionPanel__form">
            <div className="hdr-search-search search-submission">
              <label htmlFor={"searchSubmission"}>
                {control_labels.search_submission_label}
              </label>
              <label className="sr-only" htmlFor="searchSubmissionButton">
                Search
              </label>
              <div className="searchHolder">
                <input
                  id="searchSubmission"
                  type="text"
                  className="form-control"
                  value={state.keyword}
                  onChange={handleChange}
                  placeholder={messages.searchBar}
                />
                <button
                  id="searchSubmissionButton"
                  type="button"
                  className="search-button"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
            </div>
            <div className="hdr-search-search">
              <DropdownNonForm
                label={control_labels.filter_label}
                id="filter"
                name="filter"
                onChange={handleFilterChange}
                options={status_options}
                value={state.status}
                isClearable={false}
                size="md"
              />
            </div>
            <div className="submission-btn-panel">
              <Button role="presentation" onClick={handleRefresh} isLink>
                {actions.refresh}
              </Button>

              <NewQuoteButton />
            </div>
          </div>
        </div>
        <div className="row quotes">
          <div className="col-sm-12 col-md-12 col-lg-12 m-t">
            {messages.note}
          </div>
        </div>
        <div className="row submission-result-panel">
          {isLoading ? (
            <LoadingIndicator />
          ) : clonedFiltered && clonedFiltered.length > 0 ? (
            <>
              <>
                {
                  /**
                   * THIS ALERT IS TEMPORARY UNTIL WE REMOVE THE OLD PRO QUOTE CODE
                   * show the alert if the list has any old ProQuote quotes and the LD flag is ON
                   */
                  disableOldProQuote &&
                    clonedFiltered.some(
                      item => item?.core_system === "OnlineQuoting"
                    ) && (
                      <Alert
                        type="warning"
                        dismissible={false}
                        className="mt-1 mb-2"
                      >
                        We have updated our system and some quotes are no longer
                        editable. Please start a new quote or contact your
                        underwriter for assistance.&nbsp;&nbsp;
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href="https://myemail.constantcontact.com/Time-saving-BOP-changes.html?soid=1115349612465&aid=645ucRjRSik"
                        >
                          Click here for more information
                        </a>
                      </Alert>
                    )
                }
              </>
              <>
                {(clonedFiltered || [])
                  .sort(effectiveDateSort)
                  .map((obj, i) => {
                    //The plan was for customers to always exist in the NBC database.
                    //However, the New Quote feature isn't done and we want to test GW integration.
                    //So for now we will relax that limitation and show all.
                    if (
                      obj?.Customer !== undefined &&
                      obj?.Customer?.id === undefined
                    ) {
                      obj.Customer.id =
                        priorCustomer !== undefined &&
                        priorCustomer?.name === obj?.Customer?.name
                          ? priorCustomer?.id
                          : v4();
                    }

                    if (
                      priorCustomer !== undefined &&
                      priorCustomer?.id === obj?.Customer?.id
                    ) {
                      return null;
                    }
                    priorCustomer = obj.Customer;
                    return (
                      <div key={obj?.Customer?.id + v4()}>
                        <SubmissionResults
                          index={i}
                          result={obj}
                          submissionList={clonedFiltered}
                          handleRefresh={handleRefresh}
                        />
                      </div>
                    );
                  })}
              </>
            </>
          ) : (
            <p>
              <HelpDeskMessage
                display="questionsOrAssistance"
                before="We're sorry but we are unable to locate your submission based on the information you provided. Please create a new submission."
              />
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

NewSubmissionsContainer.propTypes = {
  submissionList: PropTypes.array,
  agencyCode: PropTypes.string.isRequired,
  fetchSubmissionList: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};
export default connect(null, {})(NewSubmissionsContainer);
